import orderBy from 'lodash/orderBy'
import last from 'lodash/last'
import {
    bufferCache as cachedClips,
    cachedBuffer as cache,
    makeBufferKey
} from '@/services/cached-buffer';

export function playingClip(state) {
    return state.playingClipId
        ? state.clips.find(clip => clip.id == state.playingClipId)
        : null
}

export function pausedClip(state) {
    return state.isPaused;
}

export function isWorkingClipNew(state) {
    return (state.workingClip !== null && state.workingClip.id === null);
}

/**
 * Check whether the user has moved the clip scrubber without saving since loading.
 * @param state
 * @param getters
 * @returns boolean
 */
export function isClipScrubberEdited(state, getters) {
    if (state.workingClip === null) {
        return false;
    }

    const workingClip = state.workingClip

    if (getters.isWorkingClipNew) {
        return workingClip.start_time !== null || workingClip.end_time !== null;
    }

    const existingClip = state.clips.find(clip => clip.id == workingClip.id)
    return (workingClip.start_time != existingClip.start_time || workingClip.end_time != existingClip.end_time);
}

/**
 * Check if the user has not saved progress.
 * @param state
 * @param getters
 * @returns boolean
 */
export function isUnsavedProgress(state, getters) {
    return getters.isClipScrubberEdited;
}

export function getVideo(state) {
    return videoId => state.videos.find(v => v.id == videoId)
}

export function getClipById(state) {
    return id => state.clips.find(c => c.id == id)
}

export function clipsInPlayOrder(state) {
    return orderBy(state.clips, 'order', 'asc')
}

export function playlistDuration(state, getters) {
    return getters
        .playlist
        .map(clip => {
            const start = clip.start_time || 0
            const end = clip.end_time || clip.video.durationInMilliseconds
            return end - start
        })
        .reduce((total, duration) => total + duration, 0)
}

export function isPlaying(state) {
    return !!state.playingClipId
}

export function isLoading(state) {
    return state.isLoading;
}

export function isProcessing(state) {
    return state.processingStack.length > 0
}

export function processingText(state) {
    return last(state.processingStack)
}

export function isSaving(state) {
    return state.isSaving;
}

export function isClipUpdating(state) {
    return state.isClipUpdating;
}

export function isLibraryExpanded(state) {
    return state.isLibraryExpanded;
}

export function activeProject(state) {
    return state.projects.find(({ id }) => id === state.activeProjectId);
}

export function unfinishedTranscodingJobs(state) {
    const unfinishedStatus = ['started', 'created', 'running', 'queued', 'progressing', 'waiting'];
    return state.transcodingJobs.filter(job => unfinishedStatus.includes(job.status) || job.willBeRetried);
}

export function failedTranscodingJobs(state) {
    return state.transcodingJobs.filter(job => job.status === "failed" && !job.willBeRetried);
}

export const errorForPublishingField = (state) => (field) => {
    return state.publishingErrors[field] ? state.publishingErrors[field][0] : '';
}

export function isWorkingClipPanoramic(state) {
    return state.workingClip !== null && state.workingClip.video.category === '180degrees';
}

export function panoramicCapture(state, getters) {
    let panoCapture = getters.isWorkingClipPanoramic ? state.workingClip.video.captures.find(({ captureType }) => captureType === 'pano') : null;

    if (panoCapture && !cachedClips.hasOwnProperty(makeBufferKey({video: panoCapture}, true))) {
        cache({video: panoCapture}, true);
    }

    return panoCapture;
}

export function clipUploadTranscodingJobs(state) {
    const clipUploads = state.clips.filter(({ aws_upload_id }) => !!aws_upload_id).map(({ aws_upload_id }) => Number(aws_upload_id));

    return state.transcodingJobs.filter(({ uploads }) => uploads.filter(({ id }) => clipUploads.includes(Number(id))).length >= 1);
}

export function clipUploadUnfinishedTranscodingJobs(state, getters) {
    return getters.clipUploadTranscodingJobs.filter(({ status, willBeRetried }) => ['created', 'waiting', 'started', 'running', 'queued'].includes(status) || willBeRetried);
}

export function clipUploadFailedTranscodingJobs(state, getters) {
    return getters.clipUploadTranscodingJobs.filter(({ status, willBeRetried }) => ['failed'].includes(status) && !willBeRetried);
}

export function clipsWithFailedTranscodingJob(state, getters) {
    const failedUploadsIds = getters
        .clipUploadFailedTranscodingJobs
        .reduce((acc, job) => [...acc, ...job.uploads.map(upload => Number(upload.id))], []);
    return state.clips.filter(clip => !!clip.aws_upload_id && failedUploadsIds.includes(Number(clip.aws_upload_id)));
}

export function playableClips(state, getters) {
    return getters.clipsInPlayOrder.filter(clip => {
        if (clip.aws_upload_id) {
            return !getters.clipUploadFailedTranscodingJobs.find(({ uploads }) => uploads.find(({ id }) => Number(id) === Number(clip.aws_upload_id)))
        }

        return true
    })
}

export function hasDeletedVideos(state) {
    return state.clips.some(({ video }) => video?.deleted)
}

// Playlist only includes playable clips
export function playlist(state, getters) {
    return getters.playableClips.filter(c => !!c.video && !c.video.deleted)
}

export function workingClipLength(state) {
    if (state.workingClip === null) {
        return null
    }
    const endTime = state.workingClip.end_time ?? state.workingClip.video.durationInMilliseconds
    const startTime = state.workingClip.start_time ?? 0
    return endTime - startTime
}

export function hasShortWorkingClip(state, getters) {
    if (state.workingClip === null) {
        return false
    }

    return getters.workingClipLength < 900
}
